import styles from './PageHeadingWithSync.module.scss';
import {
  BreadcrumbHeading,
  Export,
  Plus,
  PrimaryButton,
  Refresh,
  SecondaryButton,
  TimerIcon,
  ViewIcon,
} from '@weareopenr/component-library';
import { formatDate } from '../../helpers/formatDate/formatDate';
import { formatTime } from '../../helpers/formatTime/formatTime';
import { IEndpoint } from '../../types/aggregator.types';
import { useEffect, useMemo, useState } from 'react';
import { getSyncEndpoint } from 'src/helpers/getSyncEndpoint/getSyncEndpoint';
import useBreadcrumbs from 'src/hooks/useBreadcrumbs/useBreadcrumbs';
import { publishProducts } from 'src/api/publishProducts/publishProducts';
import { useExternalSystems } from 'src/hooks/useExternalSystems';
import { ProductPublishModal } from '../ProductPublishModal/ProductPublishModal';
import { useAuthWrapper } from '@weareopenr/authkit';
import { IntegrationType } from '../../constants';
import { PublishProductsType } from 'src/api/publishProducts/types';
import { useEvents } from '../../hooks/useEvents';
import { EVENT_TYPES } from '../../api/getEvents';
import { CancelScheduledProductPublicationPrompt } from '../CancelScheduledProductPublicationPrompt/CancelScheduledProductPublicationPrompt';
import { IProductLibraryStatus } from 'src/hooks/useProductLibraryStatus/useProductLibraryStatus';
import { useNavigate, useLocation } from 'react-router-dom';
import { EProductLibraryStatus } from 'src/api/getProductLibrarySyncStatus';
import { SyncReportModal } from '../SyncReportModal/SyncReportModal';

const getLastSyncTime = (endpoint?: IEndpoint): Date | null => {
  if (endpoint?.lastUpdated) {
    return new Date(endpoint.lastUpdated);
  }
  return null;
};

export interface IPageHeadingWithSyncProps {
  isSyncEnabled: boolean;
  isSyncInProgress: boolean;
  triggerSync: () => void;
  onPlusClick: () => void;
  syncInfo?: IProductLibraryStatus;
  rejectSync: () => void;
}

export const PageHeadingWithSync = ({
  isSyncEnabled,
  onPlusClick,
  isSyncInProgress,
  triggerSync,
  syncInfo,
  rejectSync,
}: IPageHeadingWithSyncProps) => {
  const { data: externalSystems } = useExternalSystems();
  const { featureFlags } = useAuthWrapper();
  const navigate = useNavigate();
  const location = useLocation();

  const searchParams = new URLSearchParams(location.search);
  const reviewModalRequested = searchParams.get('review') === 'true';

  const syncEndpoint: IEndpoint = useMemo(() => getSyncEndpoint(externalSystems), [externalSystems]);
  const isProductSyncEnabled = useMemo(() => !!syncEndpoint && isSyncEnabled, [syncEndpoint]);
  const lastSyncTime: Date | null = getLastSyncTime(syncEndpoint);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [isCancelPublishPromptOpen, setIsCancelPublishPromptOpen] = useState<boolean>(false);
  const [isSyncReportModalOpen, setIsSyncReportModalOpen] = useState<boolean>(false);

  const [isProductPublishOpen, setIsProductPublishOpen] = useState<boolean>(false);

  const isPendingReview = syncInfo?.sync_status === EProductLibraryStatus.PENDING_REVIEW;

  useEffect(() => {
    if (reviewModalRequested && isPendingReview) {
      setIsSyncReportModalOpen(true);
    }
  }, [reviewModalRequested, isPendingReview]);

  const closeSyncReportModal = () => {
    setIsSyncReportModalOpen(false);
    if (reviewModalRequested) {
      navigate(location.pathname);
    }
  };

  const isPosSystemEnabled = externalSystems?.some(
    (agg) => agg.integrationType === IntegrationType.POINT_OF_SALE_MANAGEMENT,
  );
  const breadcrumbs = useBreadcrumbs();
  const { data: events } = useEvents({ event_type: EVENT_TYPES.PUBLISH }, { enabled: isPosSystemEnabled });

  const { isPublishScheduled, scheduledPublishEvent, latestPublishEvent } = useMemo(() => {
    if (!events?.data?.length) {
      return { isPublishScheduled: false, scheduledPublishEvent: null, latestPublishEvent: null };
    }

    const scheduledEvent = events.data.find((event) => event.status === 'SCHEDULED') || null;
    const latestEvent = scheduledEvent || events.data[0] || null;

    return {
      isPublishScheduled: !!scheduledEvent,
      scheduledPublishEvent: scheduledEvent,
      latestPublishEvent: latestEvent,
    };
  }, [events]);

  const handlePublishClick = async () => {
    if (featureFlags.includes('product_publish_flow')) {
      setIsProductPublishOpen(true);
    } else {
      try {
        setIsLoading(true);
        await publishProducts({ publish_type: [PublishProductsType.PRODUCT] });
      } catch (error) {
        console.error('Error publishing products:', error);
      } finally {
        setIsLoading(false);
      }
    }
  };

  const handleCancelScheduledPublish = async () => {
    setIsCancelPublishPromptOpen((prev) => !prev);
  };

  const addProduct = {
    name: 'New product',
    onOptionClick: onPlusClick,
    id: 'add-product',
    isDisabled: isSyncInProgress,
    icon: Plus,
  };

  const viewPublishStatus = {
    name: 'View Publish Status',
    onOptionClick: () => latestPublishEvent && navigate(`${location.pathname}/status/${latestPublishEvent.event_id}`),
    id: 'view-publish-status',
    isDisabled: isSyncInProgress || !latestPublishEvent,
    icon: ViewIcon,
  };

  return (
    <>
      <SyncReportModal
        isOpen={isSyncReportModalOpen}
        closeModal={closeSyncReportModal}
        products={syncInfo?.diff}
        rejectSync={rejectSync}
      />
      <ProductPublishModal isOpen={isProductPublishOpen} closeModal={() => setIsProductPublishOpen(false)} />
      <CancelScheduledProductPublicationPrompt
        event={scheduledPublishEvent}
        isOpen={isCancelPublishPromptOpen}
        closePrompt={handleCancelScheduledPublish}
      />
      <div className={styles['info-container']}>
        <BreadcrumbHeading
          breadcrumbs={breadcrumbs}
          optionMenuItems={isPosSystemEnabled ? [addProduct, viewPublishStatus] : [addProduct]}
          primaryButton={
            isPendingReview ? (
              <PrimaryButton onClick={() => setIsSyncReportModalOpen(true)} icon={Refresh}>
                Review Sync
              </PrimaryButton>
            ) : (
              isPosSystemEnabled && (
                <>
                  {isPublishScheduled ? (
                    <PrimaryButton onClick={handleCancelScheduledPublish} icon={TimerIcon}>
                      Scheduled
                    </PrimaryButton>
                  ) : (
                    <PrimaryButton onClick={handlePublishClick} disabled={isLoading || isSyncInProgress} icon={Export}>
                      Publish
                    </PrimaryButton>
                  )}
                </>
              )
            )
          }
          secondaryButton={
            isProductSyncEnabled ? (
              isPendingReview ? (
                <SecondaryButton onClick={rejectSync} colour="tomato">
                  Reject Sync
                </SecondaryButton>
              ) : (
                <SecondaryButton onClick={triggerSync} icon={Refresh} disabled={isSyncInProgress}>
                  {isSyncInProgress ? 'Sync in Progress...' : 'Sync Products'}
                </SecondaryButton>
              )
            ) : undefined
          }
          subtitle={
            lastSyncTime
              ? `Last synchronised at ${formatDate(lastSyncTime)}, ${formatTime(lastSyncTime)}`
              : 'Not yet synchronised'
          }
        />
      </div>
    </>
  );
};
